import React from "react"
import * as Sentry from "@sentry/react"

import { openFeedbackDialog } from "Shared/components/FeedbackDialog/openFeedback"

type Reason =
  | "chunk_mismatch"
  | "html_instead_of_js"
  | "network_fetch"
  | "storage_quota"
  | "persist_corruption"
  | "unknown"

const classify = (
  err: any
): { reason: Reason; canHardReload?: boolean; suggestPurge?: boolean } => {
  const m = String(err?.message || err || "").toLowerCase()
  if (/loading chunk|chunkloaderror|css chunk|import/i.test(m))
    return { reason: "chunk_mismatch", canHardReload: true }
  if (/unexpected token</i.test(m)) return { reason: "html_instead_of_js", canHardReload: true }
  if (/failed to fetch|networkerror/i.test(m))
    return { reason: "network_fetch", canHardReload: true }
  if (/quota|indexeddb|domexception/i.test(m))
    return { reason: "storage_quota", suggestPurge: true }
  if (/json.*parse/i.test(m)) return { reason: "persist_corruption", suggestPurge: true }
  return { reason: "unknown" }
}

// Exporté pour réutilisation ailleurs si besoin
export const purgeAppStorage = async () => {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(regs.map(r => r.unregister()))
    }
    if (window.caches) {
      const keys = await caches.keys()
      await Promise.all(keys.map(k => caches.delete(k)))
    }
    localStorage.clear()
    sessionStorage.clear()
    // Best-effort IndexedDB
    // @ts-ignore
    if (window.indexedDB?.databases) {
      // @ts-ignore
      const dbs = await window.indexedDB.databases()
      await Promise.all(
        dbs.map(
          (d: any) =>
            new Promise<void>(res => {
              try {
                const req = window.indexedDB.deleteDatabase(d?.name)
                req.onsuccess = req.onerror = req.onblocked = () => res()
              } catch {
                res()
              }
            })
        )
      )
    }
  } catch {
    // silencieux
  }
}

export default class ResilientErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { err: any; eventId?: string }
> {
  state = { err: null as any, eventId: undefined as string | undefined }

  static getDerivedStateFromError(err: any) {
    return { err }
  }

  componentDidCatch(error: any, info: any) {
    const c = classify(error)
    const eventId = Sentry.captureException(error, scope => {
      scope.setTag("auto_recovery.reason", c.reason)
      if (info?.componentStack) scope.setContext("react", { componentStack: info.componentStack })
      return scope
    })
    this.setState({ eventId })
  }

  render() {
    const { err, eventId } = this.state
    if (!err) return this.props.children
    const c = classify(err)
    const softReload = () => location.reload()
    const hardReload = () => location.reload()
    const purgeAndReload = async () => {
      await purgeAppStorage()
      location.href = "/"
    }
    const reportBug = () => {
      openFeedbackDialog({ associatedEventId: eventId })
    }

    return (
      <div style={{ padding: 16 }}>
        <h3>Un problème est survenu</h3>
        <p>On peut tenter une récupération automatique.</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={softReload}>Rafraîchir</button>
          {c.canHardReload && <button onClick={hardReload}>Hard refresh</button>}
          {c.suggestPurge && <button onClick={purgeAndReload}>Vider le cache et relancer</button>}
          <button onClick={reportBug}>Signaler ce problème</button>
        </div>
        <details style={{ marginTop: 12 }}>
          <summary>Détails</summary>
          <pre style={{ whiteSpace: "pre-wrap" }}>{String(err?.stack || err)}</pre>
        </details>
      </div>
    )
  }
}
